import {
  DataBlockDto,
  DataBlockVariant,
  useDataBlockType,
} from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import {
  LinkProps,
  UniLink,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useDataBlockTypeTranslation } from '@lmt/unicorn-shared/app/components/measurements/common';
import {
  DataBlockUiEditor,
  DataBlockUiEditorProps,
} from '@lmt/unicorn-shared/app/components/measurements/DataBlockUiEditor';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import CancelIcon from '@lmt/unicorn-shared/app/icons/small-cross.svg?react';
import SaveIcon from '@lmt/unicorn-shared/app/icons/small-tick.svg?react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionnaireButton } from '../case-tree/Questionnaire';
import { DocumentsList } from '../documents/DocumentsList';
import { DataBlockOptionButton } from './DataBlockOptionButton';

import styles from './DataBlockPreview.module.scss';
import { useContextSelector } from 'use-context-selector';
import { PatientProfileContext } from '../../../types';
import { useEventCallback } from '@mui/material';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

const StyledLink = (props: LinkProps & { isVisible: boolean }) => {
  return props.isVisible ? (
    <UniLink
      className={styles.link}
      color={props.color ?? ButtonColor.Primary}
      title={props.title}
      onClick={props.onClick}
      icon={props.icon}
      disabled={props.disabled}
    />
  ) : null;
};

type DataBlockPreviewProps =
  | {
      type: 'update_data_block';
      patientId: number;
      dataBlock: DataBlockDto;
      isGDTEnabled: boolean;
      isHL7Enabled: boolean;
      isNotDeletable: boolean;
      isFirstBlock?: boolean;
      showStatistic?: boolean;
      isStatisticCompleted?: boolean;
      isReadOnly?: boolean;
    }
  | {
      type: 'update_patient_block';
      patientId: number;
      dataBlockTypeId: number;
      isFirstBlock?: boolean;
      isReadOnly?: boolean;
    };

export const DataBlockPreview: FC<DataBlockPreviewProps> = (props) => {
  const { type, isFirstBlock, isReadOnly } = props;

  const dataBlockTypeId =
    type === 'update_data_block'
      ? props.dataBlock.typeId
      : props.dataBlockTypeId;

  const modals = useModal();
  const i18next = useTranslation();
  const translateDataBlockType = useDataBlockTypeTranslation();

  const claims = useClaims();
  const selectedDataBlockType = useDataBlockType(dataBlockTypeId);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isEditable, setEditable] = useState<boolean>(false);
  const [hasUnsavedChanges, setUnsavedChanges] = useState<boolean>(false);

  const actions: DataBlockUiEditorProps['actions'] = useRef(null);

  const saveDataBlockValues = useCallback(async () => {
    setLoading(true);

    try {
      await actions.current?.save();

      setEditable(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [actions]);

  const isEditCanceled = useContextSelector(
    PatientProfileContext,
    (v) => v.isEditCanceled,
  );

  const cancelEditing = useContextSelector(
    PatientProfileContext,
    (v) => v.cancelEditing,
  );

  const setPatientDataBlocksHasUnsavedChanges = useContextSelector(
    PatientProfileContext,
    (v) => v.setUnsavedChanges,
  );

  useEffect(() => {
    if (isEditCanceled) {
      setEditable(false);
      actions?.current?.reset?.();
    }
  }, [isEditCanceled]);

  useEffect(() => {
    if (isEditable && isEditCanceled) {
      cancelEditing(false);
    }
  }, [isEditable]);

  useEffect(() => {
    setPatientDataBlocksHasUnsavedChanges(
      `${dataBlockTypeId}`,
      hasUnsavedChanges,
    );
  }, [hasUnsavedChanges]);

  const onCancel = useEventCallback(async () => {
    if (hasUnsavedChanges) {
      const isConfirmed = await modals.showConfirm({
        title: i18next.t('patients.unsaved_changes_confirmation.title'),
        text: i18next.t(
          'patients.unsaved_changes_confirmation.text_cancel_editing',
        ),
        okButtonText: i18next.t(
          'patients.unsaved_changes_confirmation.ok_text',
        ),
        cancelButtonText: i18next.t(
          'patients.unsaved_changes_confirmation.cancel_text',
        ),
      });
      if (!isConfirmed) {
        return;
      }
    }
    setEditable(false);
    actions?.current?.reset?.();
  });

  return (
    <Loading loading={isLoading} title={i18next.t('patients.cases.loading')}>
      <div className={styles.container} data-test-id="data-block-container">
        <div data-first-block={isFirstBlock} className={styles.header}>
          <div className={styles.titleContainer}>
            <div data-test-id="block-title" className={styles.title}>
              {selectedDataBlockType
                ? translateDataBlockType(selectedDataBlockType)
                : null}
            </div>
            {type === 'update_data_block' && props.showStatistic && (
              <QuestionnaireButton
                passed={props.isStatisticCompleted ?? false}
                dataBlockId={props.dataBlock.id}
                dataBlockTypeId={props.dataBlock.typeId}
              />
            )}
          </div>
          <div className={styles.controlsContainer}>
            {type === 'update_data_block' &&
              !isReadOnly &&
              (!props.isNotDeletable ||
                props.isGDTEnabled ||
                props.isHL7Enabled) && (
                <DataBlockOptionButton
                  patientId={props.patientId}
                  dataBlockId={props.dataBlock.id}
                  dataBlockTypeId={props.dataBlock.typeId}
                  isGDTEnabled={props.isGDTEnabled}
                  isNotDeletable={props.isNotDeletable}
                  isHL7Enabled={props.isHL7Enabled}
                  isMiniscreenEnabled={
                    selectedDataBlockType?.variant ===
                    DataBlockVariant.DeviceMeasurement
                  }
                />
              )}
          </div>
        </div>
        <div className={styles.dataContainer}>
          <div className={styles.recordsContainer}>
            <div className={styles.recordsHeader}>
              <div className={styles.recordsTitle}>
                {i18next.t('patients.cases.records')}
              </div>
              <div className={styles.recordsOptions}>
                <StyledLink
                  isVisible={
                    claims.permissions.dataBlockManagement &&
                    !isEditable &&
                    !isReadOnly
                  }
                  title={i18next.t('patients.cases.edit_button')}
                  onClick={() => setEditable(true)}
                />
                <StyledLink
                  isVisible={isEditable && !isReadOnly}
                  title={i18next.t('patients.cases.cancel_button')}
                  icon={<CancelIcon />}
                  onClick={onCancel}
                />
                <StyledLink
                  isVisible={isEditable && !isReadOnly}
                  title={i18next.t('patients.cases.save_button')}
                  color={ButtonColor.Secondary}
                  icon={<SaveIcon />}
                  onClick={() => saveDataBlockValues()}
                />
              </div>
            </div>
            {type === 'update_patient_block' ? (
              <DataBlockUiEditor
                dataBlockTypeId={dataBlockTypeId}
                type={'update_patient_block'}
                patientId={props.patientId}
                isEditable={isEditable}
                actions={actions}
                setUnsavedChanges={setUnsavedChanges}
              />
            ) : (
              <DataBlockUiEditor
                dataBlockTypeId={dataBlockTypeId}
                type={'update_data_block'}
                dataBlockId={props.dataBlock.id}
                isEditable={isEditable}
                actions={actions}
                setUnsavedChanges={setUnsavedChanges}
              />
            )}
          </div>
          {type === 'update_data_block' && (
            <div className={styles.documentContainer}>
              <DocumentsList
                patientId={props.patientId}
                dataBlockId={props.dataBlock.id}
                visitId={props.dataBlock.visitId ?? undefined}
                isReadOnly={isReadOnly}
              />
            </div>
          )}
        </div>
      </div>
    </Loading>
  );
};
